"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Sparkles, ChevronRight, Wine } from "lucide-react";

const BOTTLES = [
  { name: "Don Julio 1942", price: "$480", popular: true },
  { name: "Grey Goose 1.5L", price: "$420" },
  { name: "Casamigos Reposado", price: "$380" },
  { name: "Veuve Clicquot Brut", price: "$320" },
];

export default function VipTableMapBottleScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Bottle service", "Bottle service")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Two taps. ", "Dos taps. ")}
            <span className="text-white/30">{t("Sparklers loaded.", "Sparklers cargadas.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Tap the table. Tap the bottle. Bar pours it, runner brings it lit. Pre-set bundles include sparklers, mixers, cups — no scribbled note to the bar.",
              "Toca la mesa. Toca la botella. La barra la sirve, el runner la lleva encendida. Los bundles ya incluyen sparklers, mezcladores, vasos — sin nota garabateada a la barra."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[920px] mx-auto items-stretch"
        >
          {/* Step 1 — Table */}
          <Step n={1}>
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-5 h-full">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">
                {t("Tap the table", "Toca la mesa")}
              </div>
              <div className="bg-[#7C3AED]/20 border border-[#C4B5FD]/40 rounded-[14px] p-4 text-center">
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#C4B5FD" }}>
                  Selected
                </div>
                <div className="text-[24px] font-black text-white">VIP 3</div>
                <div className="text-[10px] font-bold text-white/55 mt-1">MIN $3,000 · Host: Maria</div>
              </div>
            </div>
          </Step>

          <Step n={2}>
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-5 h-full">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">
                {t("Tap the bottle", "Toca la botella")}
              </div>
              <div className="space-y-1.5">
                {BOTTLES.map((b, i) => (
                  <motion.div
                    key={b.name}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.35, delay: 0.3 + 0.08 * i }}
                    className={`flex items-center gap-2 rounded-[10px] p-2 ${
                      b.popular ? "border" : "bg-white/[0.03] border border-white/5"
                    }`}
                    style={
                      b.popular
                        ? {
                            backgroundColor: "rgba(244,114,182,0.12)",
                            borderColor: "rgba(244,114,182,0.35)",
                          }
                        : undefined
                    }
                  >
                    <Wine
                      className="w-3.5 h-3.5 shrink-0"
                      style={{ color: b.popular ? "#F472B6" : "rgba(255,255,255,0.4)" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-black text-white truncate">{b.name}</div>
                    </div>
                    <div className="text-[10px] font-black text-white tabular-nums">{b.price}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Step>

          <Step n={3}>
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-5 h-full flex flex-col">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">
                {t("Fires to bar", "Sale a la barra")}
              </div>
              <div
                className="rounded-[14px] p-4 text-center flex-1 flex flex-col justify-center"
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                }}
              >
                <Sparkles className="w-6 h-6 mx-auto mb-2 text-white" />
                <div className="text-[14px] font-black text-white">VIP 3</div>
                <div className="text-[12px] font-bold text-white/90 mt-0.5">
                  Don Julio 1942
                </div>
                <div className="text-[10px] font-bold text-white/75 mt-2 uppercase tracking-wider">
                  + Sparklers · Mixers · 4 cups
                </div>
                <div className="text-[10px] font-bold text-white/90 mt-3 tabular-nums">
                  $480 → tab
                </div>
              </div>
            </div>
          </Step>
        </motion.div>
      </div>
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="relative pt-4">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-black text-white z-10"
        style={{
          background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
        }}
      >
        {n}
      </div>
      {children}
    </div>
  );
}
